import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { salespersonsAPI } from "../services/api";
import DataTable from "../components/DataTable";
import SearchableSelect from "../components/SearchableSelect";

const DATE_FILTER_OPTIONS = [
  { value: "today",   label: "Today" },
  { value: "weekly",  label: "Last 7 Days" },
  { value: "monthly", label: "This Month" },
  { value: "custom",  label: "Custom Range" },
];

const METHOD_BADGE = {
  Visited:      'badge-success',
  'Phone Call': 'badge-info',
  Message:      'badge-warning',
  'Video Call': 'badge-info',
  Meeting:      'badge-gray',
};

const SalespersonDetails = () => {
  const { id } = useParams();
  const [salesperson, setSalesperson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  // Date filters for interactions
  const [interactionFilter, setInteractionFilter] = useState("all");
  const [interactionStartDate, setInteractionStartDate] = useState(null);
  const [interactionEndDate, setInteractionEndDate] = useState(null);

  // Date filters for orders
  const [orderFilter, setOrderFilter] = useState("all");
  const [orderStartDate, setOrderStartDate] = useState(null);
  const [orderEndDate, setOrderEndDate] = useState(null);

  const fetchSalespersonDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await salespersonsAPI.getById(id);
      setSalesperson(response.data);
    } catch (err) {
      setError("Failed to load salesperson details");
      console.error("Salesperson details error:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSalespersonDetails();
  }, [fetchSalespersonDetails]);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
    }).format(amount || 0);
  };

  /* ── date filter helpers ──────────────────────────────────────────── */
  const applyDateFilter = (items, dateField, filter, startDate, endDate) => {
    if (!items || !Array.isArray(items)) return [];
    const now = new Date();

    return items.filter(item => {
      const date = new Date(item[dateField] || item.created_at);
      if (filter === 'today') {
        return date.toDateString() === now.toDateString();
      }
      if (filter === 'weekly') {
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return date >= weekAgo;
      }
      if (filter === 'monthly') {
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
      }
      if (filter === 'custom' && startDate && endDate) {
        return date >= new Date(startDate) && date <= new Date(endDate);
      }
      return true;
    });
  };

  const filteredInteractions = applyDateFilter(
    salesperson?.interactions, 'contact_date',
    interactionFilter, interactionStartDate, interactionEndDate
  );

  const filteredOrders = applyDateFilter(
    salesperson?.orders, 'order_date',
    orderFilter, orderStartDate, orderEndDate
  );

  if (loading) {
    return (
      <div className="animate-pulse space-y-4 sm:space-y-6 px-2 sm:px-0">
        <div className="h-6 sm:h-8 bg-gray-200 rounded mb-4"></div>
        <div className="card">
          <div className="card-body space-y-4">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 sm:py-12 px-4">
        <div className="text-red-600 mb-4 text-sm sm:text-base">{error}</div>
        <button onClick={fetchSalespersonDetails} className="btn btn-primary text-sm">
          Retry
        </button>
      </div>
    );
  }

  if (!salesperson) {
    return (
      <div className="text-center py-8 sm:py-12 px-4">
        <h3 className="text-base sm:text-lg font-medium text-gray-900">Salesperson not found</h3>
        <Link to="/salespersons" className="btn btn-primary mt-4 text-sm">
          Back to Salespersons
        </Link>
      </div>
    );
  }

  const tabs = [
    { id: "overview",      name: "Overview" },
    { id: "interactions",  name: `Interactions (${salesperson.interactions?.length || 0})` },
    { id: "orders",        name: `Orders (${salesperson.orders?.length || 0})` },
  ];

  const safeName = salesperson.name?.replace(/\s+/g, '-') || 'salesperson';

  /* ── date filter UI (shared renderer) ────────────────────────────── */
  const DateFilterBar = ({ filter, onFilter, startDate, onStart, endDate, onEnd, onReset }) => (
    <div className="flex flex-wrap items-center gap-3 pb-3 border-b border-gray-100 mb-2">
      <div className="flex items-center gap-2">
        <label className="form-label text-xs mb-0 whitespace-nowrap">Date Range:</label>
        <div className="w-44">
          <SearchableSelect
            value={filter}
            onChange={onFilter}
            options={DATE_FILTER_OPTIONS}
            placeholder="All Time"
          />
        </div>
      </div>
      {filter === 'custom' && (
        <>
          <input
            type="date"
            value={startDate || ''}
            onChange={e => onStart(e.target.value)}
            className="form-input text-sm py-1"
          />
          <span className="text-gray-500 text-xs">to</span>
          <input
            type="date"
            value={endDate || ''}
            onChange={e => onEnd(e.target.value)}
            className="form-input text-sm py-1"
          />
        </>
      )}
      {filter !== 'all' && (
        <button onClick={onReset} className="btn btn-secondary text-xs py-1 inline-flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Reset
        </button>
      )}
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0">
      {/* Header */}
      <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div className="flex items-center space-x-2 sm:space-x-4">
          <Link to="/salespersons" className="btn btn-ghost p-1 sm:p-2">
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{salesperson.name}</h1>
            <p className="text-xs sm:text-sm text-gray-600">{salesperson.phone || "N/A"}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`whitespace-nowrap py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm ${
                activeTab === tab.id
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-4 sm:mt-6">

        {/* ── OVERVIEW ───────────────────────────────────────────────── */}
        {activeTab === "overview" && (
          <div className="space-y-4 sm:space-y-6">
            <div className="card">
              <div className="card-header">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Personal Information</h3>
              </div>
              <div className="card-body space-y-3 sm:space-y-4">
                {[
                  { label: 'Employee ID', value: salesperson.employee_id || 'N/A' },
                  { label: 'Email',       value: salesperson.email       || 'N/A' },
                  { label: 'Phone',       value: salesperson.phone       || 'N/A' },
                  { label: 'Created At',  value: formatDateTime(salesperson.created_at) },
                  { label: 'Updated At',  value: formatDateTime(salesperson.updated_at) },
                ].map(({ label, value }) => (
                  <div key={label} className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs sm:text-sm">
                    <span className="text-gray-600">{label}</span>
                    <span className="font-medium">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Performance Summary</h3>
              </div>
              <div className="card-body">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-primary-50 rounded-lg">
                    <div className="text-2xl font-bold text-primary-600">{salesperson.interaction_count || 0}</div>
                    <div className="text-xs text-gray-600 mt-1">Interactions</div>
                  </div>
                  <div className="text-center p-4 bg-success-50 rounded-lg">
                    <div className="text-2xl font-bold text-success-600">{salesperson.order_count || 0}</div>
                    <div className="text-xs text-gray-600 mt-1">Orders</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-lg font-bold text-purple-600 break-all">{formatCurrency(salesperson.total_sales || 0)}</div>
                    <div className="text-xs text-gray-600 mt-1">Total Sales</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── INTERACTIONS ───────────────────────────────────────────── */}
        {activeTab === "interactions" && (
          <div className="card">
            <div className="card-body">
              <DataTable
                key={`${interactionFilter}-${interactionStartDate}-${interactionEndDate}`}
                data={filteredInteractions}
                columns={[
                  {
                    key: '_no', label: '#',
                    sortable: false, export: false,
                    thClass: 'w-12',
                    render: (_, __, idx) => (
                      <span className="text-gray-400 font-mono text-xs">{idx + 1}</span>
                    ),
                  },
                  {
                    key: 'customer_name', label: 'Customer',
                    exportValue: r => r.customer_name || '',
                    render: val => val || <span className="text-gray-400 text-xs">—</span>,
                  },
                  {
                    key: 'contact_method', label: 'Method',
                    exportValue: r => r.contact_method || '',
                    render: val => (
                      <span className={`badge text-xs ${METHOD_BADGE[val] || 'badge-secondary'}`}>
                        {val || 'N/A'}
                      </span>
                    ),
                  },
                  {
                    key: 'contact_date', label: 'Date',
                    exportValue: r => formatDate(r.contact_date),
                    render: (val, row) => (
                      <div className="text-sm">
                        <div>{formatDate(val)}</div>
                        {row.scheduled_time && (
                          <div className="text-xs text-gray-500">at {row.scheduled_time}</div>
                        )}
                      </div>
                    ),
                  },
                  {
                    key: 'is_scheduled', label: 'Type',
                    exportValue: r => r.is_scheduled === 1 ? 'Scheduled' : 'Walk-in',
                    render: val => val === 1
                      ? <span className="badge badge-warning text-xs">Scheduled</span>
                      : <span className="badge badge-gray text-xs">Walk-in</span>,
                  },
                  {
                    key: 'notes', label: 'Notes',
                    exportValue: r => r.notes || '',
                    tdClass: 'max-w-xs',
                    render: val => val
                      ? <span className="text-gray-700 text-xs line-clamp-2">{val}</span>
                      : <span className="text-gray-400 text-xs">—</span>,
                  },
                ]}
                searchFields={['customer_name', 'contact_method', 'notes']}
                filename={`${safeName}-interactions`}
                title={`${salesperson.name} – Interactions`}
                loading={false}
                rowKey={row => row.id}
              >
                <DateFilterBar
                  filter={interactionFilter}
                  onFilter={setInteractionFilter}
                  startDate={interactionStartDate}
                  onStart={setInteractionStartDate}
                  endDate={interactionEndDate}
                  onEnd={setInteractionEndDate}
                  onReset={() => { setInteractionFilter('all'); setInteractionStartDate(null); setInteractionEndDate(null); }}
                />
              </DataTable>
            </div>
          </div>
        )}

        {/* ── ORDERS ─────────────────────────────────────────────────── */}
        {activeTab === "orders" && (
          <div className="card">
            <div className="card-body">
              <DataTable
                key={`${orderFilter}-${orderStartDate}-${orderEndDate}`}
                data={filteredOrders}
                columns={[
                  {
                    key: '_no', label: '#',
                    sortable: false, export: false,
                    thClass: 'w-12',
                    render: (_, __, idx) => (
                      <span className="text-gray-400 font-mono text-xs">{idx + 1}</span>
                    ),
                  },
                  {
                    key: 'customer_name', label: 'Customer',
                    exportValue: r => r.customer_name || '',
                    render: val => val || <span className="text-gray-400 text-xs">—</span>,
                  },
                  {
                    key: 'order_status', label: 'Status',
                    exportValue: r => r.order_status || '',
                    render: val => (
                      <span className={`badge text-xs ${val === 'Ordered' ? 'badge-success' : 'badge-danger'}`}>
                        {val === 'Ordered' ? 'Completed' : 'Pending'}
                      </span>
                    ),
                  },
                  {
                    key: 'order_amount', label: 'Amount (KSH)',
                    exportValue: r => parseFloat(r.order_amount || 0).toFixed(2),
                    render: val => (
                      <span className="font-semibold text-success-700">
                        {formatCurrency(val || 0)}
                      </span>
                    ),
                  },
                  {
                    key: 'order_date', label: 'Order Date',
                    exportValue: r => formatDate(r.order_date),
                    render: val => formatDate(val),
                  },
                  {
                    key: 'notes', label: 'Notes',
                    exportValue: r => r.notes || '',
                    tdClass: 'max-w-xs',
                    render: val => val
                      ? <span className="text-gray-700 text-xs line-clamp-2">{val}</span>
                      : <span className="text-gray-400 text-xs">—</span>,
                  },
                ]}
                searchFields={['customer_name', 'order_status', 'notes']}
                filename={`${safeName}-orders`}
                title={`${salesperson.name} – Orders`}
                loading={false}
                rowKey={row => row.id}
              >
                <DateFilterBar
                  filter={orderFilter}
                  onFilter={setOrderFilter}
                  startDate={orderStartDate}
                  onStart={setOrderStartDate}
                  endDate={orderEndDate}
                  onEnd={setOrderEndDate}
                  onReset={() => { setOrderFilter('all'); setOrderStartDate(null); setOrderEndDate(null); }}
                />
              </DataTable>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalespersonDetails;
